<template>
  <div class="tkt-panel">
    <!-- Encabezado -->
    <div class="tkt-panel__header">
       <div class="tkt-panel__header-top">
          <div class="tkt-panel__nav-arrows">
            <button class="tkt-panel__nav-btn" :disabled="!hasPrev" @click="navigate(-1)"><span class="mdi mdi-chevron-up"></span></button>
            <button class="tkt-panel__nav-btn" :disabled="!hasNext" @click="navigate(1)"><span class="mdi mdi-chevron-down"></span></button>
          </div>
          <div class="tkt-panel__code-group">
            <span class="tkt-panel__code">{{ detail?.code }}</span>
            <span v-if="detail" class="tkt-panel__header-time">{{ detail.relativeTime }}</span>
          </div>
          <button class="tkt-panel__close-btn" @click="$emit('close')"><span class="mdi mdi-close"></span></button>
        </div>

      <template v-if="detail">
        <div class="tkt-panel__tenant-name">{{ detail.tenantName || 'Sin inquilino' }}</div>
          <div class="tkt-panel__id-line">
            <span class="tkt-panel__id-tag">{{ detail.plotName }}</span>
            <span v-if="detail.plotAreaM2">{{ detail.plotAreaM2 }} m²</span>
            <span class="tkt-panel__id-sep">·</span>

            <div class="tkt-panel__control-wrap">
              <button class="tkt-panel__status-pill" :class="`tkt-panel__status-pill--${detail.status}`" @click="statusMenuOpen = !statusMenuOpen">
                {{ statusLabel(detail.status) }}
              </button>
              <div v-if="statusMenuOpen" class="tkt-panel__dropdown-menu">
                <button v-for="opt in STATUS_OPTIONS" :key="opt.value" class="tkt-panel__dropdown-item" @click="onChangeStatus(opt.value)">{{ opt.label }}</button>
              </div>
            </div>

            <div class="tkt-panel__control-wrap">
              <button class="tkt-panel__priority-pill" :style="{ background: priorityColor(detail.priority) + '22', color: priorityColor(detail.priority) }" @click="priorityMenuOpen = !priorityMenuOpen">
                {{ priorityLabel(detail.priority) }}
              </button>
              <div v-if="priorityMenuOpen" class="tkt-panel__dropdown-menu">
                <button v-for="opt in PRIORITY_OPTIONS" :key="opt.value" class="tkt-panel__dropdown-item" @click="onChangePriority(opt.value)">{{ opt.label }}</button>
              </div>
            </div>

            <span class="tkt-panel__type-tag" :class="`tkt-panel__type-tag--${detail.type}`">{{ typeLabel(detail.type) }}</span>
          
          </div>
      </template>
    </div>

    <!-- Navegación de puntos -->
    <div class="tkt-panel__dots">
      <button v-for="(block, i) in BLOCKS" :key="block.key" class="tkt-panel__dot" :class="{ 'tkt-panel__dot--active': activeBlockIndex === i }" @click="scrollToBlock(i)">
        <span v-if="block.key === 'messages' && unreadCount > 0" class="tkt-panel__dot-badge">{{ unreadCount }}</span>
      </button>
    </div>

    <div v-if="loading" class="tkt-panel__loading">Cargando...</div>

    <div v-else-if="detail" class="tkt-panel__content" ref="contentEl" @scroll="onContentScroll">
      <!-- Fecha límite (solo si el ticket tiene una) -->
      <div v-if="detail.slaDueAt" class="tkt-panel__due" :class="{ 'tkt-panel__due--late': dueIsLate }">
        <span class="mdi mdi-calendar-clock"></span>
        <span class="tkt-panel__due-label">Fecha límite</span>
        <span class="tkt-panel__due-date">{{ formatDueDate(detail.slaDueAt) }}</span>
        <span v-if="dueIsLate" class="tkt-panel__due-badge">Vencido</span>
      </div>
      <!-- Bloque 1: Descripción -->
      <section class="tkt-panel__block" :ref="(el) => setBlockRef(el, 0)" data-block="0">
        <div class="tkt-panel__block-header"><span class="tkt-panel__block-title">Descripción</span></div>    
        <p class="tkt-panel__desc-text" :class="{ 'tkt-panel__desc-text--clamped': !descExpanded }">{{ detail.description || 'Sin descripción' }}</p>
        <button v-if="!descExpanded" class="tkt-panel__block-action" @click="descExpanded = true">Ver más</button>

        <div v-if="detail.photos.length" class="tkt-panel__photo-gallery">
          <div v-for="(p, i) in detail.photos.slice(0, 4)" :key="p.id" class="tkt-panel__photo-thumb-wrap" @click="openLightbox(i)">
            <img :src="photoUrl(p.filePath)" class="tkt-panel__photo-thumb" />
            <button class="tkt-panel__photo-delete" title="Eliminar foto" @click.stop="onDeletePhoto(p.id)">
              <span class="mdi mdi-close"></span>
            </button>
            <div v-if="i === 3 && detail.photos.length > 4" class="tkt-panel__photo-overlay">+{{ detail.photos.length - 4 }}</div>
          </div>
        </div>

        <input ref="photoInputEl" type="file" accept="image/*" style="display: none" @change="onPhotoSelected" />
        <button class="tkt-panel__block-action" @click="photoInputEl?.click()">Adjuntar archivo</button>

        <div v-if="detail.linkedTickets?.length" class="tkt-panel__links">
          <span class="tkt-panel__links-title">Tickets vinculados</span>
          <div v-for="l in detail.linkedTickets" :key="l.id" class="tkt-panel__link-row">
            <button class="tkt-panel__link-open" :title="`Abrir ${l.code}`" @click="emit('navigate', l.id)">
              <span class="tkt-panel__link-code">{{ l.code }}</span>
              <span class="tkt-panel__link-title">{{ l.title }}</span>
              <span class="tkt-panel__link-meta">{{ l.plotName }} · {{ statusLabel(l.status) }}</span>
            </button>
            <button class="tkt-panel__link-remove" title="Quitar vínculo" aria-label="Quitar vínculo" @click="onUnlink(l.id)"><span class="mdi mdi-close"></span></button>
          </div>
        </div>
      </section>

      <!-- Bloque 2: Asignación -->
      <section class="tkt-panel__block" :ref="(el) => setBlockRef(el, 1)" data-block="1">
        <div class="tkt-panel__block-header">
          <span class="tkt-panel__block-title">Técnico asignado</span>
          <span v-if="detail.technician" class="tkt-panel__block-title-sub">{{ detail.technician.name }}</span>
        </div>

        <template v-if="detail.technician && !showTechnicianSelector">
          <div class="tkt-panel__tech-card">
            <span class="tkt-panel__tech-avatar" :style="{ background: AVATAR_COLORS[detail.technician.avatarColor] || detail.technician.avatarColor }">
              {{ initials(detail.technician.name) }}
            </span>
            <div class="tkt-panel__tech-info">
              <div class="tkt-panel__tech-name">{{ detail.technician.name }}</div>
              <div class="tkt-panel__tech-cargo">{{ detail.technician.cargo || '—' }}</div>
            </div>
            <div class="tkt-panel__tech-metrics">
              <span class="tkt-panel__tech-metric-main">{{ detail.technician.activeTicketCount }} activos</span>
              <span class="tkt-panel__tech-metric-sub">{{ detail.technician.avgResolutionHours !== null ? `${detail.technician.avgResolutionHours}h prom.` : '—' }}</span>
            </div>
          </div>
          <div v-if="assignmentState" class="tkt-panel__assign" :style="{ color: assignmentState.color, borderColor: assignmentState.color }">
            <span class="mdi" :class="assignmentState.icon"></span>
            <span class="tkt-panel__assign-label">{{ assignmentState.label }}</span>
            <span class="tkt-panel__assign-detail">{{ assignmentState.detail }}</span>
          </div>
          <button class="tkt-panel__block-action" @click="showTechnicianSelector = true">Cambiar técnico</button>
        </template>

        <div v-if="!detail.technician || showTechnicianSelector" class="tkt-panel__tech-selector">
          <button
            v-for="opt in detail.technicianOptions"
            :key="opt.id"
            class="tkt-panel__tech-option"
            @click="onAssignTechnician(opt.id)"
          >
            <span class="tkt-panel__tech-avatar tkt-panel__tech-avatar--sm" :style="{ background: AVATAR_COLORS[opt.avatarColor] || opt.avatarColor }">
              {{ initials(opt.name) }}
            </span>
            <div class="tkt-panel__tech-option-info">
              <div class="tkt-panel__tech-option-name">{{ opt.name }}</div>
              <div class="tkt-panel__tech-option-count">
                {{ opt.activeTicketCount }} tickets activos
                <span v-if="opt.acceptingTickets === false" class="tkt-panel__tech-paused" title="Marcó que no acepta tickets por ahora">· En pausa</span>
              </div>
              <div class="tkt-panel__tech-capacity-bar">
                <div class="tkt-panel__tech-capacity-fill" :class="capacityColorClass(opt.capacityPercent)" :style="{ width: opt.capacityPercent + '%' }"></div>
              </div>
            </div>
          </button>
        </div>
      </section>

      <div v-if="assignmentNotificationInfo && assignmentNotificationInfo.channel !== 'WHATSAPP'" class="tkt-panel__wa-notif tkt-panel__wa-notif--email">
        <span class="mdi mdi-email-outline"></span>
        Notificación de asignación enviada por correo
      </div>

      <!-- Bloque 3: Hilo de mensajes -->
      <section class="tkt-panel__block" :ref="(el) => setBlockRef(el, 2)" data-block="2">
        <div class="tkt-panel__block-header">
          <span class="tkt-panel__block-title">Seguimiento</span>
          <span class="tkt-panel__block-title-sub">({{ detail.messages.length }} mensajes)</span>
          <button class="tkt-panel__mute-btn" @click="muted = !muted">
            <span class="mdi" :class="muted ? 'mdi-bell-off-outline' : 'mdi-bell-outline'"></span>
          </button>
        </div>

        <div class="tkt-panel__thread">
        <div
            v-for="m in detail.messages"
            :key="m.id"
            class="tkt-panel__message"
            :class="[
              `tkt-panel__message--${m.authorRole.toLowerCase()}`,
              { 'tkt-panel__message--internal': m.isInternal }
            ]"
          >
            <span v-if="m.isAutomated" class="tkt-panel__message-auto-tag">Automático</span>
            <span v-if="m.isInternal" class="mdi mdi-lock-outline tkt-panel__message-lock"></span>
            <span v-if="m.authorRole === 'TENANT'" class="tkt-panel__message-tag">Inquilino</span>
            <div class="tkt-panel__message-text">{{ m.text }}</div>
            <div v-if="m.triggeredStatusChange" class="tkt-panel__message-status-note">
              Esta respuesta actualizó el estatus del ticket a {{ statusLabel(m.triggeredStatusChange) }}.
            </div>
            <div v-if="m.attachmentPath" class="tkt-panel__message-attachment">
              <span class="mdi mdi-paperclip"></span> {{ m.attachmentName }}
            </div>
            <div class="tkt-panel__message-meta">
              {{ m.author }} · {{ formatDateTime(m.createdAt) }}
              <span v-if="m.readByAdmin" class="mdi mdi-check-all tkt-panel__message-read"></span>
            </div>
          </div>
          <div v-if="!detail.messages.length" class="tkt-panel__thread-empty">Sin mensajes todavía</div>
        </div>

        <div class="tkt-panel__composer" :class="{ 'tkt-panel__composer--internal': isInternalMessage }">
          <label class="tkt-panel__internal-toggle">
            <input type="checkbox" v-model="isInternalMessage" />
            <span class="mdi mdi-lock-outline"></span> Mensaje interno
          </label>         
          <span v-if="isInternalMessage" class="tkt-panel__internal-hint">Solo visible para el equipo</span>

          <div class="tkt-panel__composer-row">
            <textarea
              v-model="messageText"
              class="tkt-panel__composer-input"
              :placeholder="isInternalMessage ? 'Escribe una nota interna del equipo...' : 'Escribe una actualización para el inquilino...'"
              rows="1"
              @keydown.enter.exact.prevent="submitMessage"
            ></textarea>
            <input ref="messageFileEl" type="file" style="display: none" @change="onMessageFileSelected" />
            <button class="tkt-panel__composer-clip" @click="messageFileEl?.click()"><span class="mdi mdi-paperclip"></span></button>
            <button class="tkt-panel__composer-send" :disabled="!messageText.trim() && !messageFile" @click="submitMessage">
              <span class="mdi mdi-send"></span>
            </button>
          </div>
        </div>
      </section>

      <!-- Bloque 4: Costos -->
      <section class="tkt-panel__block" :ref="(el) => setBlockRef(el, 3)" data-block="3">
        <div class="tkt-panel__block-header">
          <span class="tkt-panel__block-title">Costos</span>
          <span class="tkt-panel__block-title-sub">{{ formatCurrency(detail.costsTotal) }}</span>
        </div>

        <table class="tkt-panel__cost-table">
          <thead>
            <tr><th>Concepto</th><th>Cant.</th><th>P. unit.</th><th>Subtotal</th><th></th></tr>
          </thead>
          <tbody>
            <template v-for="c in detail.costs" :key="c.id">
              <tr>
                <td>
                  {{ c.concept }} <span class="tkt-panel__cost-cat">{{ costCategoryLabel(c.category) }}</span>
                  <span v-if="c.billedToTenant" class="tkt-panel__cost-billed-tag">Cobrado al inquilino</span>
                </td>
                <td>{{ c.quantity }}</td>
                <td>{{ formatCurrency(c.unitPrice) }}</td>
                <td>{{ formatCurrency(c.subtotal) }}</td>
                <td class="tkt-panel__cost-actions">
                  <button v-if="c.billedToTenant" type="button" class="tkt-panel__cost-btn" title="Quitar cobro al inquilino" aria-label="Quitar cobro al inquilino" @click="askCostAction(c.id, 'unbill')"><span class="mdi mdi-cash-remove"></span></button>
                  <button v-else-if="detail.tenantName" type="button" class="tkt-panel__cost-btn" title="Cobrar al inquilino" aria-label="Cobrar al inquilino" @click="askCostAction(c.id, 'bill')"><span class="mdi mdi-cash-plus"></span></button>
                  <button type="button" class="tkt-panel__cost-btn tkt-panel__cost-btn--danger" title="Eliminar costo" aria-label="Eliminar costo" @click="askCostAction(c.id, 'delete')"><span class="mdi mdi-trash-can-outline"></span></button>
                </td>
              </tr>
              <tr v-if="costConfirm.id === c.id">
                <td colspan="5" class="tkt-panel__cost-confirm">
                  <p class="tkt-panel__cost-confirm-text">
                    <template v-if="costConfirm.action === 'unbill'">¿Quitar el cobro al inquilino? El costo se queda registrado como gasto del parque.</template>
                    <template v-else-if="costConfirm.action === 'bill'">¿Cobrar este costo ({{ formatCurrency(c.subtotal) }}) al inquilino? Se genera un cargo en Cobranza y deja de contar como gasto del parque.</template>
                    <template v-else>¿Eliminar este costo? Si estaba marcado para cobro, también se retira el cobro al inquilino.</template>
                  </p>
                  <p v-if="c.chargeAmountPaid > 0 && costConfirm.action !== 'bill'" class="tkt-panel__cost-confirm-warn">
                    El inquilino ya pagó {{ formatCurrency(c.chargeAmountPaid) }} de este cobro. Si continúas, el cobro se retira y ese dinero queda como <strong>saldo a favor</strong> en su cuenta (se aplicará a su siguiente cargo pendiente). Se le avisará por correo.
                  </p>
                  <p v-if="costError" class="tkt-panel__cost-confirm-error">{{ costError }}</p>
                  <div class="tkt-panel__cost-confirm-actions">
                    <button type="button" class="tkt-panel__cost-confirm-cancel" :disabled="costBusy" @click="cancelCostAction">Cancelar</button>
                    <button type="button" class="tkt-panel__cost-confirm-ok" :disabled="costBusy" @click="confirmCostAction(c)">{{ costBusy ? 'Guardando...' : (c.chargeAmountPaid > 0 && costConfirm.action !== 'bill' ? 'Sí, dejar como saldo a favor' : 'Sí, continuar') }}</button>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
          <tfoot>
            <tr><td colspan="3">Total</td><td>{{ formatCurrency(detail.costsTotal) }}</td></tr>
          </tfoot>
        </table>

        <div class="tkt-panel__cost-split">
          <div><span>Se cobra al inquilino</span><strong>{{ formatCurrency(detail.costsBilledTotal) }}</strong></div>
          <div><span>Gasto del parque</span><strong>{{ formatCurrency(detail.costsTotal - detail.costsBilledTotal) }}</strong></div>
        </div>

        <div v-if="showAddCost" class="tkt-panel__add-cost-form">
          <select v-model="costForm.category" class="tkt-panel__input"><option value="LABOR">Mano de obra</option><option value="MATERIALS">Materiales</option><option value="EXTERNAL">Externos</option></select>
          <input v-model="costForm.concept" placeholder="Concepto" class="tkt-panel__input" />
          <input v-model.number="costForm.quantity" type="number" placeholder="Cantidad" class="tkt-panel__input" />
          <input v-model.number="costForm.unitPrice" type="number" placeholder="Precio unitario" class="tkt-panel__input" />
          <label class="tkt-panel__cost-billed-checkbox" :title="detail.tenantName ? '' : 'La nave no tiene inquilino'"><input type="checkbox" v-model="costForm.billedToTenant" :disabled="!detail.tenantName" /> Cobrar al inquilino</label>
          <p v-if="!detail.tenantName" class="tkt-panel__cost-hint">Esta nave no tiene inquilino: el costo se registra como gasto del parque y aparece en Finanzas.</p>
          <button class="tkt-panel__form-submit" @click="submitCostLine">Guardar</button>
        </div>
        <button class="tkt-panel__block-action" @click="showAddCost = !showAddCost">＋ Agregar línea</button>
      </section>

      <!-- Bloque 5: Información de la nave -->
      <section class="tkt-panel__block" :ref="(el) => setBlockRef(el, 4)" data-block="4">
        <div class="tkt-panel__block-header"><span class="tkt-panel__block-title">Nave e inquilino</span></div>
        <div class="tkt-panel__plot-info">
          <div v-if="detail.plotInfo.internalLocation" class="tkt-panel__plot-info-row"><span class="mdi mdi-map-marker-outline"></span> {{ detail.plotInfo.internalLocation }}</div>
          <div v-if="detail.plotInfo.accessNotes" class="tkt-panel__plot-info-row"><span class="mdi mdi-key-outline"></span> {{ detail.plotInfo.accessNotes }}</div>
          <div v-if="detail.plotInfo.operationalContact" class="tkt-panel__plot-info-row">
            <span class="mdi mdi-account-outline"></span> {{ detail.plotInfo.operationalContact.name }}
            <span v-if="detail.plotInfo.operationalContact.phone"> · {{ detail.plotInfo.operationalContact.phone }}</span>
          </div>
        </div>

        <div v-if="detail.plotHistory.length" class="tkt-panel__plot-history">
          <span class="tkt-panel__subtitle">Historial de tickets (6 meses)</span>
          <div v-for="h in detail.plotHistory" :key="h.code" class="tkt-panel__plot-history-row">
            <span class="mdi" :class="h.resolved ? 'mdi-check-circle tkt-panel__history-resolved' : 'mdi-circle-outline tkt-panel__history-pending'"></span>
            {{ h.code }} · {{ typeLabel(h.type) }}
          </div>
        </div>
      </section>

      <!-- Bloque 6: Log de auditoría -->
      <section class="tkt-panel__block" :ref="(el) => setBlockRef(el, 5)" data-block="5">
        <div class="tkt-panel__block-header">
          <span class="tkt-panel__block-title">Historial de cambios</span>
          <button class="tkt-panel__block-action" @click="auditExpanded = !auditExpanded">{{ auditExpanded ? 'Ocultar' : 'Mostrar' }}</button>
        </div>
        <div v-if="auditExpanded" class="tkt-panel__audit-list">
          <div v-for="(a, i) in detail.auditLog" :key="i" class="tkt-panel__audit-row">
            <span class="tkt-panel__audit-text">{{ a.text }}</span>
            <span class="tkt-panel__audit-meta">{{ formatDateTime(a.createdAt) }}</span>
          </div>
        </div>
      </section>
    </div>

    <!-- Barra de acciones -->
    <div v-if="detail" class="tkt-panel__actions">
      <button
        class="tkt-panel__action-primary"
        :class="{ 'tkt-panel__action-primary--reassign': detail.technician }"
        @click="showTechnicianSelector = true; scrollToBlock(1)"
      >
        {{ detail.technician ? 'Reasignar' : 'Asignar técnico' }}
      </button>
      <button class="tkt-panel__action-secondary" :disabled="!detail.technician" @click="showResolveModal = true">Marcar resuelto</button>
      <div class="tkt-panel__more-wrap">
        <button class="tkt-panel__more-btn" @click="moreMenuOpen = !moreMenuOpen"><span class="mdi mdi-dots-horizontal"></span></button>
        <div v-if="moreMenuOpen" class="tkt-panel__more-menu">
          <button @click="scrollToBlock(1); moreMenuOpen = false">Cambiar prioridad</button>
          <button @click="openEscalate">Escalar a dirección</button>
          <button @click="openDuplicate">Duplicar ticket</button>
          <button @click="openLinkModal">Vincular con otro ticket</button>
          <button @click="showCancelModal = true; moreMenuOpen = false">Cancelar ticket</button>
        </div>
      </div>
    </div>

    <!-- Modal resolver -->
    <div v-if="showResolveModal" class="tkt-panel__modal-backdrop" @click="showResolveModal = false">
      <div class="tkt-panel__modal" @click.stop>
        <h4>Confirmar resolución</h4>
        <p>Costo total registrado: {{ formatCurrency(detail.costsTotal) }}</p>
        <div class="tkt-panel__modal-actions">
          <button class="tkt-panel__modal-btn tkt-panel__modal-btn--secondary" @click="showResolveModal = false">Cancelar</button>
          <button class="tkt-panel__modal-btn" @click="confirmResolve">Confirmar</button>
        </div>
      </div>
    </div>

    <!-- Modal: motivo de espera -->
    <div v-if="showWaitModal" class="tkt-panel__modal-backdrop" @click="showWaitModal = false">
      <div class="tkt-panel__modal" @click.stop>
        <h4>Motivo de la espera</h4>
        <select v-model="waitForm.reason" class="tkt-panel__input">
          <option value="PARTS">Espera de refacción</option>
          <option value="TENANT_APPROVAL">Espera de aprobación del inquilino</option>
          <option value="EXTERNAL_VENDOR">Espera de proveedor externo</option>
          <option value="OTHER">Otros</option>
        </select>
        <input v-if="waitForm.reason === 'OTHER'" v-model="waitForm.note" placeholder="Describe el motivo" class="tkt-panel__input" />
        <div class="tkt-panel__modal-actions">
          <button class="tkt-panel__modal-btn tkt-panel__modal-btn--secondary" @click="showWaitModal = false">Cancelar</button>
          <button class="tkt-panel__modal-btn" @click="confirmWait">Confirmar</button>
        </div>
      </div>
    </div>

    <!-- Modal escalar -->
    <div v-if="showEscalateModal" class="tkt-panel__modal-backdrop" @click="showEscalateModal = false">
      <div class="tkt-panel__modal" @click.stop>
        <h4>Escalar a dirección</h4>
        <p>Se deja constancia en la conversación interna y se avisa en las notificaciones del parque. La prioridad no cambia.</p>
        <input v-model="escalateReason" placeholder="Motivo (opcional)" class="tkt-panel__input" maxlength="500" />
        <p v-if="actionError" class="tkt-panel__action-error">{{ actionError }}</p>
        <div class="tkt-panel__modal-actions">
          <button class="tkt-panel__modal-btn tkt-panel__modal-btn--secondary" :disabled="actionBusy" @click="showEscalateModal = false">Cerrar</button>
          <button class="tkt-panel__modal-btn" :disabled="actionBusy" @click="confirmEscalate">{{ actionBusy ? 'Escalando...' : 'Escalar' }}</button>
        </div>
      </div>
    </div>

    <!-- Modal duplicar -->
    <div v-if="showDuplicateModal" class="tkt-panel__modal-backdrop" @click="showDuplicateModal = false">
      <div class="tkt-panel__modal" @click.stop>
        <h4>Duplicar ticket</h4>
        <p>Se crea un ticket nuevo y abierto en la misma nave, con el mismo título, descripción, prioridad y tipo. No copia técnico, costos, fotos ni mensajes, no le escribe al inquilino y queda vinculado con este ticket.</p>
        <p v-if="actionError" class="tkt-panel__action-error">{{ actionError }}</p>
        <div class="tkt-panel__modal-actions">
          <button class="tkt-panel__modal-btn tkt-panel__modal-btn--secondary" :disabled="actionBusy" @click="showDuplicateModal = false">Cerrar</button>
          <button class="tkt-panel__modal-btn" :disabled="actionBusy" @click="confirmDuplicate">{{ actionBusy ? 'Duplicando...' : 'Duplicar' }}</button>
        </div>
      </div>
    </div>

    <!-- Modal vincular -->
    <div v-if="showLinkModal" class="tkt-panel__modal-backdrop" @click="showLinkModal = false">
      <div class="tkt-panel__modal tkt-panel__modal--wide" @click.stop>
        <h4>Vincular con otro ticket</h4>
        <input v-model="linkQuery" placeholder="Buscar por título, nave o código (MNT-2026-ABC)" class="tkt-panel__input" @input="onLinkQueryInput" />
        <div class="tkt-panel__link-results">
          <p v-if="linkLoading" class="tkt-panel__link-empty">Buscando...</p>
          <p v-else-if="!linkResults.length" class="tkt-panel__link-empty">No hay tickets que coincidan.</p>
          <button v-for="r in linkResults" :key="r.id" class="tkt-panel__link-result" :disabled="actionBusy" @click="confirmLink(r.id)">
            <span class="tkt-panel__link-code">{{ r.code }}</span>
            <span class="tkt-panel__link-title">{{ r.title }}</span>
            <span class="tkt-panel__link-meta">{{ r.plotName }} · {{ statusLabel(r.status) }}</span>
          </button>
        </div>
        <p v-if="actionError" class="tkt-panel__action-error">{{ actionError }}</p>
        <div class="tkt-panel__modal-actions">
          <button class="tkt-panel__modal-btn tkt-panel__modal-btn--secondary" @click="showLinkModal = false">Cerrar</button>
        </div>
      </div>
    </div>

    <!-- Modal cancelar -->
    <div v-if="showCancelModal" class="tkt-panel__modal-backdrop" @click="showCancelModal = false">
      <div class="tkt-panel__modal" @click.stop>
        <h4>Cancelar ticket</h4>
        <input v-model="cancelReason" placeholder="Motivo de la cancelación" class="tkt-panel__input" />
        <div class="tkt-panel__modal-actions">
          <button class="tkt-panel__modal-btn tkt-panel__modal-btn--secondary" @click="showCancelModal = false">Cerrar</button>
          <button class="tkt-panel__modal-btn tkt-panel__modal-btn--danger" @click="confirmCancel">Confirmar cancelación</button>
        </div>
      </div>
    </div>

    <!-- Lightbox -->
    <div v-if="lightboxIndex !== null" class="tkt-panel__lightbox" @click="lightboxIndex = null">
      <button class="tkt-panel__lightbox-nav tkt-panel__lightbox-nav--left" @click.stop="lightboxIndex = Math.max(0, lightboxIndex - 1)"><span class="mdi mdi-chevron-left"></span></button>
      <img :src="photoUrl(detail.photos[lightboxIndex].filePath)" class="tkt-panel__lightbox-img" @click.stop />
      <button class="tkt-panel__lightbox-nav tkt-panel__lightbox-nav--right" @click.stop="lightboxIndex = Math.min(detail.photos.length - 1, lightboxIndex + 1)"><span class="mdi mdi-chevron-right"></span></button>
      <button class="tkt-panel__lightbox-delete" @click.stop="onDeletePhoto(detail.photos[lightboxIndex].id)">
        <span class="mdi mdi-trash-can-outline"></span> Eliminar foto
      </button>
    </div>

    <!-- Toast tiempo real -->
    <Transition name="tkt-toast">
      <div v-if="toastMessage" class="tkt-panel__toast">{{ toastMessage }}</div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { assignmentView } from './assignmentUi'
import {
  getTicketDetail,
  updateTicketStatus,
  assignTechnician,
  updateTicketPriority,
  sendTicketMessage,
  addCostLine,
  updateCostLine,
  deleteCostLine,
  archiveTicket,
  escalateTicket,
  duplicateTicket,
  getTicketLinkCandidates,
  linkTicket,
  unlinkTicket,
  uploadTicketPhoto,
  deleteTicketPhoto,
  photoUrl,
} from '@/services/maintenance'

const props = defineProps({
  ticketId: { type: String, required: true },
  ticketList: { type: Array, default: () => [] },
})

const emit = defineEmits(['close', 'navigate', 'refresh'])

const AVATAR_COLORS = { blue: '#3b82f6', green: '#10b981', violet: '#8b5cf6', orange: '#f97316', teal: '#14b8a6', pink: '#ec4899', indigo: '#6366f1', amber: '#f0a500' }
const BLOCKS = [{ key: 'description' }, { key: 'assignment' }, { key: 'messages' }, { key: 'costs' }, { key: 'plot' }, { key: 'audit' }]
const STATUS_OPTIONS = [{ value: 'OPEN', label: 'Abierto' }, { value: 'IN_PROGRESS', label: 'En proceso' }, { value: 'WAITING', label: 'En espera' }, { value: 'RESOLVED', label: 'Resuelto' }]
const PRIORITY_OPTIONS = [{ value: 'LOW', label: 'Baja' }, { value: 'MEDIUM', label: 'Media' }, { value: 'HIGH', label: 'Alta' }, { value: 'CRITICAL', label: 'Crítica' }]

const detail = ref(null)
const loading = ref(true)
const statusMenuOpen = ref(false)
const priorityMenuOpen = ref(false)
const descExpanded = ref(false)
const showTechnicianSelector = ref(false)
const isInternalMessage = ref(false)
const messageText = ref('')
const messageFile = ref(null)
const showAddCost = ref(false)
const costForm = ref({ category: 'LABOR', concept: '', quantity: 1, unitPrice: null, billedToTenant: false })
const auditExpanded = ref(false)
const moreMenuOpen = ref(false)
const showResolveModal = ref(false)
const showWaitModal = ref(false)
const waitForm = ref({ reason: 'PARTS', note: '' })
const showCancelModal = ref(false)
const cancelReason = ref('')
const lightboxIndex = ref(null)
const muted = ref(false)
const toastMessage = ref(null)
const assignmentNotificationInfo = ref(null)


async function loadDetail(isPoll = false) {
  if (!isPoll) loading.value = true
  try {
    const previousMessageCount = detail.value?.messages.length || 0
    const newDetail = await getTicketDetail(props.ticketId)
    if (isPoll && newDetail.messages.length > previousMessageCount && !muted.value) {
      const newest = newDetail.messages[0]
      showToast(`Nuevo mensaje de ${newest.author}`)
    }
    detail.value = newDetail
  } finally {
    loading.value = false
  }
}

let pollHandle = null
onMounted(() => {
  loadDetail()
  pollHandle = setInterval(() => loadDetail(true), 8000)
})
onUnmounted(() => clearInterval(pollHandle))
watch(() => props.ticketId, () => loadDetail())

function showToast(text) {
  toastMessage.value = text
  setTimeout(() => { toastMessage.value = null }, 3000)
}

// --- Navegación entre tickets ---
const currentIndex = computed(() => props.ticketList.findIndex((t) => t.id === props.ticketId))
const hasPrev = computed(() => currentIndex.value > 0)
const hasNext = computed(() => currentIndex.value >= 0 && currentIndex.value < props.ticketList.length - 1)
function navigate(delta) {
  const next = props.ticketList[currentIndex.value + delta]
  if (next) emit('navigate', next.id)
}

// --- Estatus / prioridad ---
function statusLabel(s) { return STATUS_OPTIONS.find((o) => o.value === s)?.label || s }
function priorityLabel(p) { return PRIORITY_OPTIONS.find((o) => o.value === p)?.label || p }
function priorityColor(p) { return { CRITICAL: '#e84545', HIGH: '#f97316', MEDIUM: '#f0a500', LOW: '#94a3b8' }[p] || '#94a3b8' }
function typeLabel(t) { return { CORRECTIVE: 'Correctivo', PREVENTIVE: 'Preventivo', IMPROVEMENT: 'Mejora' }[t] || t }

async function onChangeStatus(status) {
  statusMenuOpen.value = false
  // Poner un ticket en espera exige decir por qué (el tablero Kanban también lo pregunta).
  if (status === 'WAITING') {
    waitForm.value = { reason: 'PARTS', note: '' }
    showWaitModal.value = true
    return
  }
  try {
    await updateTicketStatus(props.ticketId, { status })
    await loadDetail()
    emit('refresh')
  } catch (error) {
    alert(error.message)
  }
}

async function confirmWait() {
  showWaitModal.value = false
  try {
    await updateTicketStatus(props.ticketId, { status: 'WAITING', waitReason: waitForm.value.reason, waitReasonNote: waitForm.value.note })
    await loadDetail()
    emit('refresh')
  } catch (error) {
    alert(error.message)
  }
}

async function onChangePriority(priority) {
  priorityMenuOpen.value = false
  await updateTicketPriority(props.ticketId, priority)
  await loadDetail()
  emit('refresh')
}

// --- Técnico ---
function initials(name) { return name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase() }
function capacityColorClass(pct) { return pct >= 85 ? 'tkt-panel__tech-capacity-fill--red' : pct >= 60 ? 'tkt-panel__tech-capacity-fill--amber' : 'tkt-panel__tech-capacity-fill--green' }

async function onAssignTechnician(technicianId) {
  const result = await assignTechnician(props.ticketId, technicianId)
  assignmentNotificationInfo.value = { channel: result.notificationChannel }
  showTechnicianSelector.value = false
  await loadDetail()
  emit('refresh')
}

// --- Mensajes ---
const messageFileEl = ref(null)
function onMessageFileSelected(e) { messageFile.value = e.target.files[0] || null }

async function submitMessage() {
  if (!messageText.value.trim() && !messageFile.value) return
  await sendTicketMessage(props.ticketId, { text: messageText.value, isInternal: isInternalMessage.value, file: messageFile.value, channel: 'PLATFORM' })
  messageText.value = ''
  messageFile.value = null
  await loadDetail()
}

const assignmentState = computed(() => assignmentView(detail.value?.assignment))
const unreadCount = computed(() => detail.value?.messages.filter((m) => !m.readByAdmin).length || 0)

// --- Fotos ---
const photoInputEl = ref(null)
async function onPhotoSelected(e) {
  const file = e.target.files[0]
  if (!file) return
  await uploadTicketPhoto(props.ticketId, file)
  e.target.value = ''
  await loadDetail()
}
function openLightbox(i) { lightboxIndex.value = i }

async function onDeletePhoto(photoId) {
  if (!confirm('¿Eliminar esta foto? Esta acción no se puede deshacer.')) return
  await deleteTicketPhoto(photoId)
  lightboxIndex.value = null
  await loadDetail()
}

// --- Costos ---
async function submitCostLine() {
  if (!costForm.value.concept || !costForm.value.unitPrice) return
  await addCostLine(props.ticketId, costForm.value)
  showAddCost.value = false
  costForm.value = { category: 'LABOR', concept: '', quantity: 1, unitPrice: null, billedToTenant: false }
  await loadDetail()
  emit('refresh')
}
const costConfirm = ref({ id: null, action: null })
const costBusy = ref(false)
const costError = ref('')

function askCostAction(id, action) {
  costConfirm.value = { id, action }
  costError.value = ''
}

function cancelCostAction() {
  costConfirm.value = { id: null, action: null }
  costError.value = ''
}

async function confirmCostAction(cost) {
  costBusy.value = true
  costError.value = ''
  try {
    const keep = cost.chargeAmountPaid > 0
    if (costConfirm.value.action === 'unbill') await updateCostLine(cost.id, { billedToTenant: false, keepPaidAsCredit: keep })
    else if (costConfirm.value.action === 'bill') await updateCostLine(cost.id, { billedToTenant: true })
    else await deleteCostLine(cost.id, { keepPaidAsCredit: keep })
    cancelCostAction()
    await loadDetail()
    emit('refresh')
  } catch (err) {
    costError.value = err.message
  } finally {
    costBusy.value = false
  }
}

function costCategoryLabel(c) { return { LABOR: 'Mano de obra', MATERIALS: 'Materiales', EXTERNAL: 'Externos' }[c] || c }

// --- Resolver / cancelar ---
async function confirmResolve() {
  showResolveModal.value = false
  try {
    await updateTicketStatus(props.ticketId, { status: 'RESOLVED' })
    await loadDetail()
    emit('refresh')
  } catch (error) {
    alert(error.message)
  }
}

async function confirmCancel() {
  await archiveTicket(props.ticketId, cancelReason.value)
  showCancelModal.value = false
  cancelReason.value = ''
  emit('refresh')
  emit('close')
}

// --- Menú «···»: escalar, duplicar y vincular ---
const showEscalateModal = ref(false)
const showDuplicateModal = ref(false)
const showLinkModal = ref(false)
const escalateReason = ref('')
const actionBusy = ref(false)
const actionError = ref('')
const linkQuery = ref('')
const linkResults = ref([])
const linkLoading = ref(false)

function openEscalate() {
  moreMenuOpen.value = false
  actionError.value = ''
  escalateReason.value = ''
  showEscalateModal.value = true
}

async function confirmEscalate() {
  actionBusy.value = true
  actionError.value = ''
  try {
    await escalateTicket(props.ticketId, escalateReason.value)
    showEscalateModal.value = false
    await loadDetail(true)
    showToast('Ticket escalado a dirección')
  } catch (e) {
    actionError.value = e.message
  } finally {
    actionBusy.value = false
  }
}

function openDuplicate() {
  moreMenuOpen.value = false
  actionError.value = ''
  showDuplicateModal.value = true
}

async function confirmDuplicate() {
  actionBusy.value = true
  actionError.value = ''
  try {
    const copy = await duplicateTicket(props.ticketId)
    showDuplicateModal.value = false
    emit('refresh')
    emit('navigate', copy.id) // se abre el ticket nuevo
  } catch (e) {
    actionError.value = e.message
  } finally {
    actionBusy.value = false
  }
}

let linkTimer = null
async function searchLinks() {
  linkLoading.value = true
  try {
    linkResults.value = await getTicketLinkCandidates(props.ticketId, linkQuery.value)
  } catch (e) {
    actionError.value = e.message
    linkResults.value = []
  } finally {
    linkLoading.value = false
  }
}
function onLinkQueryInput() {
  clearTimeout(linkTimer)
  linkTimer = setTimeout(searchLinks, 250)
}
function openLinkModal() {
  moreMenuOpen.value = false
  actionError.value = ''
  linkQuery.value = ''
  linkResults.value = []
  showLinkModal.value = true
  searchLinks()
}
async function confirmLink(otherId) {
  actionBusy.value = true
  actionError.value = ''
  try {
    await linkTicket(props.ticketId, otherId)
    showLinkModal.value = false
    await loadDetail(true)
    showToast('Tickets vinculados')
  } catch (e) {
    actionError.value = e.message
  } finally {
    actionBusy.value = false
  }
}
async function onUnlink(otherId) {
  try {
    await unlinkTicket(props.ticketId, otherId)
    await loadDetail(true)
  } catch (e) {
    showToast(e.message)
  }
}

// --- Navegación de bloques ---
const contentEl = ref(null)
const blockRefs = ref([])
const activeBlockIndex = ref(0)
function setBlockRef(el, i) { if (el) blockRefs.value[i] = el }
function scrollToBlock(i) { blockRefs.value[i]?.scrollIntoView({ behavior: 'smooth', block: 'start' }) }
function onContentScroll() {
  if (!contentEl.value) return
  const el = contentEl.value
  const scrollTop = el.scrollTop
  const maxScrollTop = el.scrollHeight - el.clientHeight

  let closest = 0
  let closestDist = Infinity
  blockRefs.value.forEach((elRef, i) => {
    if (!elRef) return
    const effectiveOffset = Math.min(elRef.offsetTop, maxScrollTop)
    const dist = Math.abs(effectiveOffset - scrollTop)
    if (dist < closestDist) {
      closestDist = dist
      closest = i
    }
  })
  activeBlockIndex.value = closest
}

// --- Formato ---
function formatCurrency(n) { return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(n || 0) }
const dueIsLate = computed(() => !!detail.value?.slaDueAt && detail.value.status !== 'RESOLVED' && new Date(detail.value.slaDueAt) < new Date())
function formatDueDate(iso) { return new Date(iso).toLocaleDateString('es-MX', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) }
function formatDateTime(iso) { return new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) }
</script>

<style scoped>
.tkt-panel {
  flex: 0 0 40%;
  max-width: 40%;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: 1px solid var(--border-active);
  border-radius: 10px;
  overflow: hidden;
  margin: 0.75rem 1.5rem 1.5rem 0;
  position: relative;
  min-height: 0;
}

.tkt-panel__loading { padding: 2rem; text-align: center; color: var(--text-secondary); font-family: var(--font-body); font-size: 13px; }

.tkt-panel__header {
  flex-shrink: 0;
  padding: 0.75rem 1.25rem 1.1rem calc(1.25rem + 12px);
  border-bottom: 1px solid #f1f5f9;
  height: auto;
  min-height: 96px;
  box-sizing: border-box;
}.tkt-panel__header-top { display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.35rem; }
.tkt-panel__nav-arrows { display: flex; flex-direction: column; }
.tkt-panel__nav-btn { background: none; border: none; color: #94a3b8; cursor: pointer; display: flex; padding: 0; line-height: 0.7; }
.tkt-panel__nav-btn:hover:not(:disabled) { color: #1e3a5f; }
.tkt-panel__nav-btn:disabled { opacity: 0.3; }
.tkt-panel__code { flex: 1; text-align: center; font-family: var(--font-mono); font-size: 11px; color: #94a3b8; }
.tkt-panel__close-btn { background: none; border: none; color: #94a3b8; cursor: pointer; display: flex; }

.tkt-panel__cost-hint { margin: 0; font-size: 11px; line-height: 1.4; color: #64748b; }
.tkt-panel__tenant-name { font-family: var(--font-display); font-weight: 700; font-size: 18px; color: #1e3a5f; }
.tkt-panel__id-line {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.4rem;
  font-family: var(--font-body);
  font-size: 12px;
  color: #64748b;
  margin-top: 0.15rem;
}.tkt-panel__id-tag { background: #f1f5f9; border-radius: 4px; padding: 0.05rem 0.35rem; }

.tkt-panel__controls-row { display: flex; gap: 0.5rem; margin-top: 0.4rem; }
.tkt-panel__control-wrap { position: relative; }
.tkt-panel__status-pill, .tkt-panel__priority-pill { border-radius: 20px; padding: 0.2rem 0.6rem; font-family: var(--font-body); font-size: 11px; font-weight: 500; border: none; cursor: pointer; }
.tkt-panel__status-pill--OPEN { background: #dbeafe; color: #2563eb; }
.tkt-panel__status-pill--IN_PROGRESS { background: #fef3c7; color: #d97706; }
.tkt-panel__status-pill--WAITING { background: #ffedd5; color: #ea580c; }
.tkt-panel__status-pill--RESOLVED { background: #dcfce7; color: #16a34a; }

.tkt-panel__dropdown-menu { position: absolute; top: 100%; left: 0; margin-top: 4px; background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; box-shadow: 0 8px 24px rgba(15,23,42,0.12); z-index: 50; min-width: 150px; padding: 0.3rem; }
.tkt-panel__dropdown-item { display: block; width: 100%; text-align: left; background: none; border: none; font-family: var(--font-body); font-size: 12px; color: var(--text-content); padding: 0.4rem 0.5rem; cursor: pointer; border-radius: 5px; }
.tkt-panel__dropdown-item:hover { background: #f8fafc; }

.tkt-panel__meta-line { font-family: var(--font-body); font-size: 10px; color: #94a3b8; margin-top: 0.4rem; display: flex; align-items: center; gap: 0.3rem; }
.tkt-panel__type-tag { font-size: 9px; border-radius: 4px; padding: 0.05rem 0.3rem; }
.tkt-panel__type-tag--CORRECTIVE { background: #fee2e2; color: #dc2626; }
.tkt-panel__type-tag--PREVENTIVE { background: #dbeafe; color: #1d4ed8; }
.tkt-panel__type-tag--IMPROVEMENT { background: #ede9fe; color: #6d28d9; }

.tkt-panel__dots { position: absolute; left: 4px; top: 100px; bottom: 60px; width: 4px; display: flex; flex-direction: column; justify-content: center; gap: 10px; z-index: 10; }
.tkt-panel__dot { width: 6px; height: 6px; border-radius: 50%; background: #e2e8f0; border: none; cursor: pointer; padding: 0; position: relative; }
.tkt-panel__dot--active { background: #3b82f6; }
.tkt-panel__dot-badge { position: absolute; left: 10px; top: -4px; background: #3b82f6; color: #fff; font-size: 8px; border-radius: 8px; min-width: 12px; padding: 0 3px; }

.tkt-panel__content { flex: 1; min-height: 0; overflow-y: auto; padding-left: 12px; }
.tkt-panel__content::-webkit-scrollbar { width: 6px; }
.tkt-panel__content::-webkit-scrollbar-thumb { background: var(--border-active); border-radius: 3px; }
.tkt-panel__content::-webkit-scrollbar-track { background: transparent; }

.tkt-panel__block { padding: 1rem 1.25rem; border-bottom: 1px solid #f1f5f9; }
.tkt-panel__block-header { display: flex; align-items: center; gap: 0.4rem; margin-bottom: 0.6rem; }
.tkt-panel__block-title { font-family: var(--font-body); font-size: 11px; font-weight: 500; text-transform: uppercase; letter-spacing: 0.8px; color: #94a3b8; }
.tkt-panel__block-title-sub { font-family: var(--font-body); font-size: 10px; color: #3b82f6; }
.tkt-panel__block-action { background: none; border: none; color: #3b82f6; font-family: var(--font-body); font-size: 12px; cursor: pointer; padding: 0; margin-top: 0.5rem; display: block; }
.tkt-panel__mute-btn { margin-left: auto; background: none; border: none; color: #94a3b8; cursor: pointer; display: flex; }

.tkt-panel__due { display: flex; align-items: center; gap: 0.5rem; padding: 0.55rem 0.75rem; margin-bottom: 0.75rem; background: #f1f5f9; border-radius: 8px; font-family: var(--font-body); font-size: 12px; color: #475569; }
.tkt-panel__due .mdi { font-size: 16px; color: #64748b; }
.tkt-panel__due-label { font-weight: 500; }
.tkt-panel__due-date { margin-left: auto; color: #1e3a5f; font-weight: 500; }
.tkt-panel__due-badge { background: #e84545; color: #fff; font-size: 10px; font-weight: 600; padding: 1px 8px; border-radius: 10px; }
.tkt-panel__due--late { background: #fef2f2; }
.tkt-panel__due--late .mdi, .tkt-panel__due--late .tkt-panel__due-date { color: #e84545; }

.tkt-panel__wa-notif { display: flex; align-items: center; gap: 0.4rem; margin: 0 0 0.75rem; padding: 0.45rem 0.7rem; border-radius: 6px; font-family: var(--font-body); font-size: 11px; font-weight: 500; line-height: 1.3; }
.tkt-panel__wa-notif .mdi { font-size: 14px; flex-shrink: 0; }
.tkt-panel__wa-notif--wa { background: #ecfdf5; color: #047857; }
.tkt-panel__wa-notif--email { background: #eff6ff; color: #1d4ed8; }
.tkt-panel__tech-paused { color: #b45309; font-weight: 600; }

.tkt-panel__desc-text { font-family: var(--font-body); font-size: 13px; color: #1e3a5f; line-height: 1.6; white-space: pre-wrap; }
.tkt-panel__desc-text--clamped { display: -webkit-box; -webkit-line-clamp: 6; -webkit-box-orient: vertical; overflow: hidden; }

.tkt-panel__photo-gallery { display: flex; gap: 0.4rem; margin-top: 0.5rem; }
.tkt-panel__photo-thumb-wrap { position: relative; width: 80px; height: 80px; cursor: pointer; }
.tkt-panel__photo-thumb { width: 100%; height: 100%; object-fit: cover; border-radius: 6px; }
.tkt-panel__photo-overlay { position: absolute; inset: 0; background: rgba(0,0,0,0.5); color: #fff; display: flex; align-items: center; justify-content: center; font-size: 14px; border-radius: 6px; }
.tkt-panel__photo-delete {
  position: absolute;
  top: -6px;
  right: -6px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #1e293b;
  color: #fff;
  border: 2px solid #fff;
  font-size: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}
.tkt-panel__photo-delete:hover { background: #dc2626; }
.tkt-panel__assign { display: flex; align-items: center; gap: 0.4rem; margin-top: 0.5rem; padding: 0.3rem 0.5rem; border: 1px solid; border-radius: 6px; font: 500 12px Inter, sans-serif; background: #ffffff; }
.tkt-panel__assign-label { font-weight: 600; }
.tkt-panel__assign-detail { margin-left: auto; font-weight: 400; font-size: 11px; opacity: 0.85; }
.tkt-panel__tech-card { display: flex; align-items: center; gap: 0.6rem; background: #f8f9fc; border-radius: 6px; padding: 0.5rem; height: 56px; box-sizing: border-box; }
.tkt-panel__tech-avatar { width: 36px; height: 36px; border-radius: 50%; color: #fff; font-size: 12px; font-weight: 600; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.tkt-panel__tech-avatar--sm { width: 28px; height: 28px; font-size: 10px; }
.tkt-panel__tech-info { flex: 1; }
.tkt-panel__tech-name { font-family: var(--font-body); font-size: 13px; font-weight: 500; color: #1e3a5f; }
.tkt-panel__tech-cargo { font-family: var(--font-body); font-size: 11px; color: #94a3b8; }
.tkt-panel__tech-metrics { display: flex; flex-direction: column; align-items: flex-end; }
.tkt-panel__tech-metric-main { font-family: var(--font-body); font-size: 11px; font-weight: 500; color: #1e3a5f; }
.tkt-panel__tech-metric-sub { font-family: var(--font-body); font-size: 10px; color: #94a3b8; }

.tkt-panel__tech-selector { display: flex; flex-direction: column; gap: 0.4rem; margin-top: 0.5rem; }
.tkt-panel__tech-option { display: flex; align-items: center; gap: 0.5rem; background: #f8f9fc; border: none; border-radius: 6px; padding: 0.5rem; height: 44px; box-sizing: border-box; cursor: pointer; text-align: left; }
.tkt-panel__tech-option-info { flex: 1; }
.tkt-panel__tech-option-name { font-family: var(--font-body); font-size: 12px; font-weight: 500; color: #1e3a5f; }
.tkt-panel__tech-option-count { font-family: var(--font-body); font-size: 10px; color: #94a3b8; }
.tkt-panel__tech-capacity-bar { height: 4px; background: #e2e8f0; border-radius: 2px; margin-top: 0.2rem; overflow: hidden; }
.tkt-panel__tech-capacity-fill { height: 100%; }
.tkt-panel__tech-capacity-fill--green { background: #10b981; }
.tkt-panel__tech-capacity-fill--amber { background: #f0a500; }
.tkt-panel__tech-capacity-fill--red { background: #e84545; }

.tkt-panel__thread { display: flex; flex-direction: column-reverse; gap: 0.5rem; max-height: 300px; overflow-y: auto; }
.tkt-panel__thread-empty { font-family: var(--font-body); font-size: 12px; color: #94a3b8; text-align: center; padding: 1rem; }
.tkt-panel__message { max-width: 85%; border-radius: 8px; padding: 8px 10px; position: relative; }
.tkt-panel__message--admin { background: #eff6ff; border: 1px solid #bfdbfe; align-self: flex-end; margin-left: auto; }
.tkt-panel__message--technician { background: #f1f5f9; border: 1px solid #e2e8f0; align-self: flex-start; }
.tkt-panel__message--tenant { background: #fffbeb; border: 1px solid #fde68a; align-self: flex-start; }
.tkt-panel__message--internal { border-style: dashed; }
.tkt-panel__message-lock { position: absolute; top: 6px; right: 6px; font-size: 10px; color: #94a3b8; }
.tkt-panel__message-tag { font-size: 9px; color: #d97706; font-weight: 600; display: block; margin-bottom: 0.15rem; }
.tkt-panel__message-text { font-family: var(--font-body); font-size: 13px; color: #1e3a5f; white-space: pre-wrap; }
.tkt-panel__message-attachment { display: flex; align-items: center; gap: 0.2rem; font-size: 10px; color: #64748b; margin-top: 0.3rem; }
.tkt-panel__message-meta { font-family: var(--font-body); font-size: 10px; color: #94a3b8; margin-top: 0.25rem; display: flex; align-items: center; gap: 0.2rem; }
.tkt-panel__message-read { color: #3b82f6; }

.tkt-panel__composer { background: #f8f9fc; border-top: 1px solid #e2e8f0; border-radius: 6px; padding: 0.5rem; margin-top: 0.6rem; }
.tkt-panel__composer--internal { border-style: dashed; }
.tkt-panel__channel-pills { display: flex; gap: 0.4rem; margin-bottom: 0.4rem; }
.tkt-panel__channel-pill {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  padding: 0.25rem 0.65rem;
  font-family: var(--font-body);
  font-size: 11px;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
}
.tkt-panel__channel-pill--active { background: #eff6ff; border-color: #bfdbfe; color: #1d4ed8; }
.tkt-panel__wa-recipient { display: block; font-family: var(--font-body); font-size: 10px; color: #64748b; margin-bottom: 0.3rem; }
.tkt-panel__internal-toggle { display: flex; align-items: center; gap: 0.3rem; font-family: var(--font-body); font-size: 11px; color: #64748b; }
.tkt-panel__internal-hint { font-family: var(--font-body); font-size: 10px; color: #d97706; margin-left: 0.5rem; }
.tkt-panel__composer-row { display: flex; align-items: flex-end; gap: 0.4rem; margin-top: 0.3rem; }
.tkt-panel__composer-input { flex: 1; font-family: var(--font-body); font-size: 13px; border: none; background: transparent; resize: none; outline: none; }
.tkt-panel__composer-clip, .tkt-panel__composer-send { background: none; border: none; color: #94a3b8; cursor: pointer; display: flex; }
.tkt-panel__composer-send { color: #3b82f6; }
.tkt-panel__composer-send:disabled { color: #cbd5e1; cursor: default; }

.tkt-panel__cost-table { width: 100%; border-collapse: collapse; font-family: var(--font-body); font-size: 12px; }
.tkt-panel__cost-table th { text-align: left; font-size: 10px; text-transform: uppercase; color: #94a3b8; padding-bottom: 0.3rem; border-bottom: 1px solid #e2e8f0; }
.tkt-panel__cost-table td { padding: 0.4rem 0; border-bottom: 1px solid #f1f5f9; color: #1e3a5f; }
.tkt-panel__cost-table th:nth-child(n+2):nth-child(-n+4),
.tkt-panel__cost-table td:nth-child(n+2):nth-child(-n+4) { white-space: nowrap; padding-left: 0.9rem; text-align: right; }
.tkt-panel__cost-table th:nth-child(2), .tkt-panel__cost-table td:nth-child(2) { padding-left: 0.6rem; }
.tkt-panel__cost-table .tkt-panel__cost-confirm { text-align: left; padding-left: 0; white-space: normal; }
.tkt-panel__cost-cat { font-size: 9px; color: #94a3b8; margin-left: 0.3rem; }
.tkt-panel__cost-billed-tag { display: inline-block; font-size: 9px; color: #1d4ed8; background: #eff6ff; border-radius: 4px; padding: 0.05rem 0.3rem; margin-left: 0.3rem; }
.tkt-panel__cost-actions { text-align: right; white-space: nowrap; }
.tkt-panel__cost-btn { background: none; border: none; color: #94a3b8; cursor: pointer; padding: 0.1rem; font-size: 14px; }
.tkt-panel__cost-btn:hover { color: #3b82f6; }
.tkt-panel__cost-btn--danger:hover { color: #dc2626; }
.tkt-panel__cost-confirm { background: #fef2f2; border-radius: 6px; padding: 0.5rem 0.6rem !important; }
.tkt-panel__cost-confirm-text { margin: 0; font-size: 11px; line-height: 1.45; color: #7f1d1d; }
.tkt-panel__cost-confirm-error { margin: 0.3rem 0 0; font-size: 11px; color: #dc2626; }
.tkt-panel__cost-confirm-actions { display: flex; gap: 0.4rem; margin-top: 0.5rem; }
.tkt-panel__cost-confirm-cancel { height: 28px; padding: 0 0.7rem; border-radius: 6px; border: 1px solid #e2e8f0; background: #fff; color: #64748b; font-size: 11px; cursor: pointer; }
.tkt-panel__cost-confirm-ok { height: 28px; padding: 0 0.7rem; border-radius: 6px; border: none; background: #dc2626; color: #fff; font-size: 11px; cursor: pointer; }
.tkt-panel__cost-confirm-cancel:disabled, .tkt-panel__cost-confirm-ok:disabled { opacity: 0.5; cursor: default; }
.tkt-panel__cost-table tfoot td { font-weight: 700; font-family: var(--font-mono); background: #f8f9fc; }

.tkt-panel__cost-confirm-warn { margin: 0 0 0.4rem; font-family: var(--font-body); font-size: 11px; line-height: 1.45; color: #92400e; background: #fffbeb; border: 1px solid #fde68a; border-radius: 6px; padding: 0.4rem 0.5rem; }
.tkt-panel__cost-split { display: flex; flex-direction: column; gap: 0.2rem; margin-top: 0.6rem; font-family: var(--font-body); font-size: 12px; color: #1e3a5f; }
.tkt-panel__cost-split > div { display: flex; justify-content: space-between; gap: 0.5rem; }
.tkt-panel__cost-split strong { font-weight: 600; }
.tkt-panel__bill-toggle { display: flex; align-items: center; gap: 0.4rem; font-family: var(--font-body); font-size: 12px; color: #1e3a5f; margin-top: 0.6rem; }

.tkt-panel__add-cost-form { display: flex; flex-direction: column; gap: 0.4rem; background: #f8f9fc; border-radius: 8px; padding: 0.6rem; margin-top: 0.5rem; }
.tkt-panel__input { font-family: var(--font-body); font-size: 13px; color: var(--text-content); background: #ffffff; border: 1px solid #e2e8f0; border-radius: 6px; padding: 0.3rem 0.5rem; }
.tkt-panel__cost-billed-checkbox { font-family: var(--font-body); font-size: 11px; display: flex; align-items: center; gap: 0.2rem; }
.tkt-panel__form-submit { background: #3b82f6; color: #fff; border: none; border-radius: 6px; padding: 0.4rem; font-family: var(--font-body); font-size: 12px; cursor: pointer; }

.tkt-panel__plot-info { display: flex; flex-direction: column; gap: 0.35rem; }
.tkt-panel__plot-info-row { display: flex; align-items: center; gap: 0.4rem; font-family: var(--font-body); font-size: 12px; color: #1e3a5f; }
.tkt-panel__subtitle { display: block; font-family: var(--font-body); font-size: 11px; font-weight: 500; text-transform: uppercase; color: #94a3b8; margin: 0.75rem 0 0.4rem; }
.tkt-panel__plot-history-row { display: flex; align-items: center; gap: 0.4rem; font-family: var(--font-body); font-size: 12px; color: #64748b; height: 24px; }
.tkt-panel__history-resolved { color: #16a34a; font-size: 12px; }
.tkt-panel__history-pending { color: #94a3b8; font-size: 12px; }

.tkt-panel__audit-list { display: flex; flex-direction: column; }
.tkt-panel__audit-row { display: flex; justify-content: space-between; height: 32px; align-items: center; font-family: var(--font-body); font-size: 12px; }
.tkt-panel__audit-text { color: #1e3a5f; }
.tkt-panel__audit-meta { font-size: 10px; color: #94a3b8; }

.tkt-panel__actions { flex-shrink: 0; height: 56px; display: flex; align-items: center; gap: 0.5rem; padding: 0 1rem; border-top: 1px solid #e2e8f0; }
.tkt-panel__action-primary { flex: 0 0 50%; background: #3b82f6; color: #fff; border: none; border-radius: 6px; height: 36px; font-family: var(--font-body); font-size: 13px; font-weight: 500; cursor: pointer; }
.tkt-panel__action-primary--reassign { flex: 0 0 35%; background: #ffffff; border: 1px solid #3b82f6; color: #3b82f6; }
.tkt-panel__action-secondary { flex: 0 0 35%; background: #ffffff; border: 1px solid #e2e8f0; border-radius: 6px; height: 36px; color: #1e3a5f; font-family: var(--font-body); font-size: 13px; font-weight: 500; cursor: pointer; }
.tkt-panel__action-secondary:disabled { opacity: 0.4; cursor: default; }
.tkt-panel__more-wrap { position: relative; flex: 1; display: flex; justify-content: flex-end; }
.tkt-panel__more-btn { background: none; border: none; color: #94a3b8; cursor: pointer; display: flex; }
.tkt-panel__more-menu { position: absolute; bottom: 100%; right: 0; margin-bottom: 4px; background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; box-shadow: 0 8px 24px rgba(15,23,42,0.12); min-width: 200px; padding: 0.3rem; z-index: 50; }
.tkt-panel__more-menu button { display: block; width: 100%; text-align: left; background: none; border: none; font-family: var(--font-body); font-size: 12px; color: var(--text-content); padding: 0.4rem 0.5rem; cursor: pointer; border-radius: 5px; }
.tkt-panel__more-menu button:hover { background: #f8fafc; }

.tkt-panel__modal-backdrop { position: fixed; inset: 0; background: rgba(15,23,42,0.4); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.tkt-panel__modal { background: #fff; border-radius: 10px; padding: 1.25rem; min-width: 320px; display: flex; flex-direction: column; gap: 0.6rem; }
.tkt-panel__modal h4 { margin: 0; font-family: var(--font-display); font-size: 15px; color: #1e3a5f; }
.tkt-panel__modal p { margin: 0; font-family: var(--font-body); font-size: 13px; color: var(--text-content); }
.tkt-panel__modal-actions { display: flex; gap: 0.5rem; justify-content: flex-end; margin-top: 0.4rem; }
.tkt-panel__modal-btn { background: #3b82f6; color: #fff; border: none; border-radius: 6px; padding: 0.5rem 0.9rem; font-family: var(--font-body); font-size: 12px; font-weight: 500; cursor: pointer; }
.tkt-panel__modal-btn--secondary { background: none; border: 1px solid #e2e8f0; color: #64748b; }
.tkt-panel__modal-btn--danger { background: #dc2626; }
.tkt-panel__modal-btn:disabled { opacity: 0.6; cursor: default; }
.tkt-panel__modal--wide { width: 440px; max-width: 92vw; }
.tkt-panel__action-error { margin: 0; font-family: var(--font-body); font-size: 12px; color: #dc2626; }
.tkt-panel__links { margin-top: 0.9rem; display: flex; flex-direction: column; gap: 0.35rem; }
.tkt-panel__links-title { font-family: var(--font-body); font-size: 10px; text-transform: uppercase; letter-spacing: 0.06em; color: #94a3b8; }
.tkt-panel__link-row { display: flex; align-items: center; gap: 0.3rem; }
.tkt-panel__link-open, .tkt-panel__link-result { flex: 1; min-width: 0; display: flex; flex-direction: column; align-items: flex-start; gap: 1px; text-align: left; background: #f8f9fc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 0.4rem 0.6rem; cursor: pointer; font-family: var(--font-body); }
.tkt-panel__link-open:hover, .tkt-panel__link-result:hover { border-color: #3b82f6; }
.tkt-panel__link-code { font-family: var(--font-mono); font-size: 10px; color: #3b82f6; }
.tkt-panel__link-title { font-size: 12px; color: #1e3a5f; max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.tkt-panel__link-meta { font-size: 10px; color: #94a3b8; }
.tkt-panel__link-remove { background: none; border: none; color: #94a3b8; cursor: pointer; display: flex; padding: 4px; }
.tkt-panel__link-remove:hover { color: #dc2626; }
.tkt-panel__link-results { display: flex; flex-direction: column; gap: 0.35rem; max-height: 280px; overflow-y: auto; }
.tkt-panel__link-empty { color: #94a3b8 !important; font-size: 12px !important; }

.tkt-panel__lightbox { position: fixed; inset: 0; background: rgba(0,0,0,0.85); display: flex; align-items: center; justify-content: center; z-index: 2000; }
.tkt-panel__lightbox-img { max-width: 80%; max-height: 80%; border-radius: 6px; }
.tkt-panel__lightbox-nav { background: rgba(255,255,255,0.1); border: none; color: #fff; font-size: 28px; padding: 0.5rem; cursor: pointer; }
.tkt-panel__lightbox-delete {
  position: absolute;
  top: 1.2rem;
  right: 1.2rem;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  background: rgba(220, 38, 38, 0.9);
  border: none;
  border-radius: 6px;
  color: #fff;
  font-family: var(--font-body);
  font-size: 12px;
  font-weight: 500;
  padding: 0.4rem 0.7rem;
  cursor: pointer;
}
.tkt-panel__lightbox-delete:hover { background: #dc2626; }
.tkt-panel__toast { position: absolute; bottom: 66px; right: 12px; background: #1e3a5f; color: #fff; font-family: var(--font-body); font-size: 12px; padding: 0.5rem 0.75rem; border-radius: 8px; box-shadow: 0 8px 24px rgba(0,0,0,0.2); z-index: 500; }
.tkt-toast-enter-active, .tkt-toast-leave-active { transition: opacity 200ms ease, transform 200ms ease; }
.tkt-toast-enter-from, .tkt-toast-leave-to { opacity: 0; transform: translateY(8px); }
.tkt-panel__code {
  flex: 1;
  text-align: center;
  font-family: var(--font-mono);
  font-size: 11px;
  color: #94a3b8;
}

.tkt-panel__header-time {
  font-family: var(--font-body);
  font-size: 10px;
  color: #94a3b8;
  flex-shrink: 0;
}
.tkt-panel__code-group {
  flex: 1;
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 0.4rem;
}
.tkt-panel__code {
  font-family: var(--font-mono);
  font-size: 11px;
  color: #94a3b8;
}
.tkt-panel__header-time {
  font-size: 10px;
  color: #94a3b8;
  flex-shrink: 0;
}
</style>